import { Button, ButtonProps } from "../button/Button";
import "./loan-card.scss";
import classNames from "classnames";
import { IconLookup } from "@fortawesome/fontawesome-svg-core";
import { ApplicationLoanStatus } from "pages/application/applicationService";
import Badge, { BadgeColor } from "../badge/Badge";
import { formatDollarAmount } from "utilities/string";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowAltCircleRight, faCheckCircle, faClock, faPartyHorn, IconDefinition } from "@fortawesome/pro-regular-svg-icons";
import { PropertyInfo } from "pages/dashboard/home/dashboardService";
import {
  AMOUNT_PENDING,
  GENERIC_PENDING,
  LOAN_CARD_ADDRESS_UNAVAILABLE,
  LOAN_CARD_FIELD_ERROR,
  PROPERTY_PENDING,
} from "utilities/Constant";
import { GLOBAL_LINK_NO_TX_CLASS } from "utilities/globalLinkNoTx";
import { useSelector } from "react-redux";
import { selectPreferredLanguage } from "features/authSlice";
import { getPreferredLanguage, isSpanishLanguage } from "utilities/getPreferredLanguage";

enum LoanBadgeTitles {
  New = "New",
  Started = "Started",
  Incomplete = "Incomplete",
  ApplicationReceived = "Application Received",
  Processing = "Processing",
  InitialApproval = "Initial Approval",
  ClearedToClose = "Cleared to Close",
  Closed = "Closed",
  Expired = "Expired"
}

/** Field-level NAFX degrade flags for My Loans cards. */
export interface LoanCardFieldAvailability {
  streetUnavailable?: boolean;
  cityStateUnavailable?: boolean;
  loanNumberUnavailable?: boolean;
  startedUnavailable?: boolean;
  loanPurposeUnavailable?: boolean;
  amountUnavailable?: boolean;
}

const getBadgeDetails = (status: ApplicationLoanStatus): { badgeClass: BadgeColor; badgeTitle: string } => {
  switch (status) {
    case "new":
      return { badgeClass: "new", badgeTitle: LoanBadgeTitles.New };
    case "incomplete":
      return { badgeClass: "incomplete", badgeTitle: LoanBadgeTitles.Incomplete };
    case "started":
      return { badgeClass: "started", badgeTitle: LoanBadgeTitles.Started };
    case "processing":
      return { badgeClass: "processing", badgeTitle: LoanBadgeTitles.Processing };
    case "submittedToUnderwriting":
      return { badgeClass: "application-received", badgeTitle: LoanBadgeTitles.ApplicationReceived };
    case "conditionallyApproved":
    case "underReview":
    case "finalReview":
      return { badgeClass: "initial-approval", badgeTitle: LoanBadgeTitles.InitialApproval };
    case "clearedToClosed":
      return { badgeClass: "cleared-to-close", badgeTitle: LoanBadgeTitles.ClearedToClose };
    case "funded":
      return { badgeClass: "closed", badgeTitle: LoanBadgeTitles.Closed };
    case "expired":
      return { badgeClass: "closed", badgeTitle: LoanBadgeTitles.Expired };
  }
};

const getContextualMessageIcon = (contextualMessage: string): IconDefinition => {
  switch (contextualMessage) {
    case "Your disclosures are ready":
      return faCheckCircle;
    case "You have tasks pending":
      return faClock;
    case "Congratulations!":
      return faPartyHorn;
    case "Continue your application":
    default:
      return faArrowAltCircleRight;
  }
};

const FieldUnavailable = ({
  message,
  testId,
  className,
}: {
  message: string;
  testId: string;
  className?: string;
}) => (
  <span className={classNames("loanCardV2__unavailable", className)} data-testid={testId}>
    {message}
  </span>
);

const getLoanBodyKeyValuePair = (key: string, value: string, options?: { error?: boolean; noTx?: boolean; testId?: string }) => (
  <div className="loanCardV2__body__keyValuePair">
    <span className="loanCardV2__body__key label-medium-regular">{key}</span>
    {options?.error ? (
      <FieldUnavailable
        message={LOAN_CARD_FIELD_ERROR}
        testId={options.testId ?? "loan-card-field-error"}
        className="loanCardV2__body__value label-medium-medium"
      />
    ) : (
      <span
        className={classNames("loanCardV2__body__value label-medium-medium", { [GLOBAL_LINK_NO_TX_CLASS]: options?.noTx })}
        data-testid={options?.testId}
      >
        {value}
      </span>
    )}
  </div>
);

const getPropertyAddress = (propertyInfo?: PropertyInfo, fieldAvailability?: LoanCardFieldAvailability) => {
  const streetAddress = propertyInfo?.street?.toLowerCase()?.replace("tbd", "")?.trim();
  const cityStateLine = [propertyInfo?.city, propertyInfo?.state].filter(Boolean).join(" ").trim();

  return (
    <>
      {fieldAvailability?.streetUnavailable ? (
        <FieldUnavailable
          message={LOAN_CARD_ADDRESS_UNAVAILABLE}
          testId="loan-card-property-street-error"
          className={`paragraph-medium-medium address`}
        />
      ) : (
        <p className={`paragraph-medium-medium address ${GLOBAL_LINK_NO_TX_CLASS}`} data-testid="loan-card-property-street">
          {streetAddress || PROPERTY_PENDING}
        </p>
      )}
      {fieldAvailability?.cityStateUnavailable ? (
        <FieldUnavailable
          message={LOAN_CARD_FIELD_ERROR}
          testId="loan-card-property-city-state-error"
          className={`paragraph-medium-medium address`}
        />
      ) : (
        <p className={`paragraph-medium-medium address ${GLOBAL_LINK_NO_TX_CLASS}`} data-testid="loan-card-property-city-state">
          {cityStateLine}
        </p>
      )}
    </>
  );
};

interface LoanCardProps {
  propertyInfo?: PropertyInfo;
  loanPurpose: string;
  loanAmount: string;
  status: ApplicationLoanStatus;
  startedDate: string;
  index: number;
  loanNumber: string;
  handleLoanCardClick: (index: number) => void;
  applicationNumber: string;
  contextualMessage: string | undefined;
  fieldAvailability?: LoanCardFieldAvailability;
}

export default function LoanCardV2({
  status,
  propertyInfo,
  loanPurpose,
  loanAmount,
  startedDate,
  index,
  loanNumber,
  applicationNumber,
  handleLoanCardClick,
  contextualMessage,
  fieldAvailability,
}: Readonly<LoanCardProps>) {
  const preferredLanguage = useSelector(selectPreferredLanguage);
  // Always show status badge (NH application / Deal milestone). Same as happy-path My Loans cards.
  const badge = getBadgeDetails(status);
  const trimmedContextualMessage = contextualMessage?.trim();

  const buttonProps: ButtonProps = {
    label: "Select",
    onClick: () => handleLoanCardClick(index),
    disabled: false,
    className: "loanCardV2__actions-select-loan",
  };

  let displayAmount = AMOUNT_PENDING;
  if (!fieldAvailability?.amountUnavailable) {
    const formattedLoanAmount = loanAmount?.trim().startsWith("$") ? loanAmount : formatDollarAmount(loanAmount);
    displayAmount = formattedLoanAmount === "$0" ? AMOUNT_PENDING : (formattedLoanAmount ?? AMOUNT_PENDING);
  }

  const renderLoanIdentifier = () => {
    if (fieldAvailability?.loanNumberUnavailable) {
      return (
        <p className="paragraph-alt-small-regular loan-number" data-testid="loan-card-loan-number-error">
          {"Loan #"}
          <span className={`loanCardV2__unavailable ${GLOBAL_LINK_NO_TX_CLASS}`}>{LOAN_CARD_FIELD_ERROR}</span>
        </p>
      );
    }

    if (loanNumber) {
      return (
        <p className="paragraph-alt-small-regular loan-number">
          {"Loan #"}
          <span className={GLOBAL_LINK_NO_TX_CLASS} data-testid="loan-card-loan-number">
            {loanNumber}
          </span>
        </p>
      );
    }

    return (
      <p className="paragraph-alt-small-regular loan-number">
        {"Application #"}
        <span className={GLOBAL_LINK_NO_TX_CLASS} data-testid="loan-card-application-number">
          {applicationNumber ?? GENERIC_PENDING}
        </span>
      </p>
    );
  };

  return (
    <div
      className={classNames("loanCardV2", {
        "loanCardV2--spanish": isSpanishLanguage(getPreferredLanguage(preferredLanguage)),
      })}
    >
      <div className="loanCardV2__header">
        <div className="loanCardV2__header-info">
          {getPropertyAddress(propertyInfo, fieldAvailability)}
          {renderLoanIdentifier()}
        </div>
        {badge && (
          <div className="loanCardV2__header-status">
            <Badge color={badge.badgeClass} title={badge.badgeTitle} toUpperCase={false} />
          </div>
        )}
      </div>
      <div className="loanCardV2__body">
        {getLoanBodyKeyValuePair("Started", startedDate, {
          error: fieldAvailability?.startedUnavailable,
          testId: "loan-card-started",
        })}
        {getLoanBodyKeyValuePair("Loan Purpose", loanPurpose, {
          error: fieldAvailability?.loanPurposeUnavailable,
          testId: "loan-card-purpose",
        })}
        {getLoanBodyKeyValuePair("Amount", displayAmount, {
          error: fieldAvailability?.amountUnavailable,
          noTx: !fieldAvailability?.amountUnavailable,
          testId: "loan-card-amount",
        })}
      </div>
      <div className="loanCardV2__actions">
        {trimmedContextualMessage ? (
          <div className="loanCardV2__actions__contextual-message label-medium-medium">
            <div className={`loanCardV2__actions__icon-wrapper ${trimmedContextualMessage === "Congratulations!" ? "party" : ""}`}>
              <FontAwesomeIcon icon={getContextualMessageIcon(trimmedContextualMessage) as IconLookup} />
            </div>
            {trimmedContextualMessage}
          </div>
        ) : (
          <div className="loanCardV2__actions__contextual-message" aria-hidden="true" />
        )}
        <Button {...buttonProps} />
      </div>
    </div>
  );
}
